import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Pagination, Spinner, Alert, Button, ButtonGroup } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import usePermissions from '../utils/usePermissions';
import VendorModal from '../components/VendorModal';
import ConfirmDialog from '../components/ConfirmDialog';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';
import '../styles/CustomerList.css';

const VendorListPage = () => {
  const { can } = usePermissions('vendors');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showInlineNotice } = useInlineNotice();
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    column: 'name',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  
  // Get current user role
  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('edit'),
    canDelete: can('delete')
  }), [can]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);


  const fetchVendors = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/vendors?page=${page}&limit=${pagination.limit}`, {
        params: searchTerm ? { search: searchTerm } : undefined,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setVendors(response.data.vendors);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(`Failed to load vendors: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    setSelectedRows([]);
    fetchVendors(1, activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handlePageChange = (page) => {
    fetchVendors(page, activeSearchTerm);
  };

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchVendors(pagination.page, activeSearchTerm);
  };

  const handleCheckboxChange = (vendorId) => {
    setSelectedRows(prevSelected => {
      if (prevSelected.includes(vendorId)) {
        return prevSelected.filter(id => id !== vendorId);
      } else {
        return [...prevSelected, vendorId];
      }
    });
  };

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      column,
      direction: prevConfig.column === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Add new vendor
  const handleAddVendor = () => {
    if (!permissions.canCreate) return;
    setSelectedVendor(null);
    setShowModal(true);
  };

  // View selected vendor
  const handleViewVendor = () => {
    if (selectedRows.length === 1) {
      const vendor = vendors.find(v => v._id === selectedRows[0]);
      setSelectedVendor(vendor);
      setShowModal(true);
    }
  };

  // Edit selected vendor
  const handleEditVendor = () => {
    if (selectedRows.length === 1) {
      if (!permissions.canEdit) return;
      const vendor = vendors.find(v => v._id === selectedRows[0]);
      setSelectedVendor(vendor);
      setShowModal(true);
    }
  };

  // Delete vendor
  const handleDeleteVendor = () => {
    if (selectedRows.length === 1 && permissions.canDelete) {
      const vendor = vendors.find(v => v._id === selectedRows[0]);
      setVendorToDelete(vendor);
      setShowDeleteConfirm(true);
    }
  };

  const handleSaveVendor = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      if (selectedVendor && !permissions.canEdit) {
        setError('You do not have permission to edit vendors.');
        return;
      }

      if (!selectedVendor && !permissions.canCreate) {
        setError('You do not have permission to add vendors.');
        return;
      }
      if (selectedVendor) {
        // Update existing vendor
        await axios.put(
          `${API_BASE_URL}/vendors/${selectedVendor._id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const name = selectedVendor.name || 'Vendor';
        showInlineNotice(`Vendor ${name} updated successfully.`, { scope: 'vendors' });
      } else {
        // Create new vendor
        await axios.post(
          `${API_BASE_URL}/vendors`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const name = formData.name || 'Vendor';
        showInlineNotice(`Vendor ${name} created successfully.`, { scope: 'vendors' });
      }
      
      // Refresh the vendor list
      fetchVendors(pagination.page, activeSearchTerm);
      setShowModal(false);
    } catch (err) {
      console.error('Error saving vendor:', err);
      showInlineNotice('Failed to save vendor.', {
        scope: 'vendors',
        type: 'error'
      });
      throw err; // Re-throw to be handled by the modal
    }
  };

  const confirmDeleteVendor = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/vendors/${vendorToDelete._id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const name = vendorToDelete?.name || 'Vendor';
      showInlineNotice(`Vendor ${name} deleted successfully.`, { scope: 'vendors' });
      setSelectedRows([]);
      // Refresh the vendor list
      fetchVendors(pagination.page, activeSearchTerm);
      setShowDeleteConfirm(false);
      setVendorToDelete(null);
    } catch (err) {
      console.error('Error deleting vendor:', err);
      setError(err.response?.data?.error || 'Failed to delete vendor');
      showInlineNotice('Failed to delete vendor.', {
        scope: 'vendors',
        type: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Vendors <span className="text-muted">({pagination.total})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="vendors" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={handleReload}
                disabled={loading}
                className="recent-invoices-btn recent-invoices-btn-reload"
              >
                {loading ? (
                  <>
                    <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                    Reload
                  </>
                )}
              </button>
              <button 
                onClick={handleViewVendor}
                disabled={selectedRows.length !== 1}
                className="recent-invoices-btn recent-invoices-btn-view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                View
              </button>
              {permissions.canEdit && (
                <button 
                  onClick={handleEditVendor}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-copy"
                  title="Edit selected vendor"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  Edit
                </button>
              )}
              {permissions.canDelete && (
                <button 
                  onClick={handleDeleteVendor}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-delete"
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                  Delete
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by Name, Address, Phone"
                disabled={loading}
                isSearching={loading && Boolean(activeSearchTerm)}
              />
              {permissions.canCreate && (
                <button
                  onClick={handleAddVendor}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th style={{width: '3%'}}></th>
              <th style={{width: '5%'}}>#</th>
              <th onClick={() => handleSort('name')} style={{width: '18%', cursor: 'pointer'}}>Name {sortConfig.column === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th style={{width: '28%'}}>Address</th>
              <th style={{width: '12%'}}>Phone</th>
              <th style={{width: '12%'}}>GST</th>
              <th style={{width: '15%'}}>Email</th>
              <th onClick={() => handleSort('relationship')} style={{width: '10%', cursor: 'pointer'}}>Relation {sortConfig.column === 'relationship' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor, index) => (
              <tr 
                key={vendor._id}
                className={selectedRows.includes(vendor._id) ? 'selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(vendor._id)}
                    onChange={() => handleCheckboxChange(vendor._id)}
                  />
                </td>
                <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal'}}>{vendor.name}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal'}}>{vendor.address || '-'}</td>
                <td style={{fontSize: '0.85rem'}}>{vendor.phone || '-'}</td>
                <td style={{fontSize: '0.85rem'}}>{vendor.gst || '-'}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal', fontSize: '0.85rem'}}>{vendor.email || '-'}</td>
                <td style={{fontSize: '0.8rem'}}>{vendor.relationship || 'VENDOR'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={pagination.page === 1}
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(pagination.page - 1)} 
              disabled={pagination.page === 1}
            />
            {[...Array(pagination.pages)].map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={idx + 1 === pagination.page}
                onClick={() => handlePageChange(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next 
              onClick={() => handlePageChange(pagination.page + 1)} 
              disabled={pagination.page === pagination.pages}
            />
            <Pagination.Last 
              onClick={() => handlePageChange(pagination.pages)} 
              disabled={pagination.page === pagination.pages}
            />
          </Pagination>
        </div>
      )}
      </div>

      {/* Vendor Modal */}
      <VendorModal
        show={showModal}
        onHide={() => setShowModal(false)}
        vendor={selectedVendor}
        onSave={handleSaveVendor}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && vendorToDelete && (
        <ConfirmDialog
          show={showDeleteConfirm}
          title="Delete Vendor"
          message={`Are you sure you want to delete "${vendorToDelete.name}"? This action cannot be undone.`}
          onConfirm={confirmDeleteVendor}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setVendorToDelete(null);
          }}
          variant="danger"
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default VendorListPage;
